// hero-beams.js
// Animated WebGL beam background for the hero section.
// Vanilla Three.js port — no React/Fiber required.
// Loaded as type="module" from hero-showcase.liquid when use_beams is enabled.

import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.165.0/build/three.module.js';

(function () {
  var mount = document.getElementById('hs-beams-mount');
  if (!mount) return;

  // ── Config (override via data-* attributes on #hs-beams-mount) ───────
  var BEAM_WIDTH      = parseFloat(mount.dataset.beamWidth      || '2');
  var BEAM_HEIGHT     = parseFloat(mount.dataset.beamHeight     || '15');
  var BEAM_NUMBER     = parseInt(  mount.dataset.beamNumber     || '12', 10);
  var LIGHT_COLOR     =            mount.dataset.lightColor     || '#c9a84c';
  var SPEED           = parseFloat(mount.dataset.speed          || '2');
  var NOISE_INTENSITY = parseFloat(mount.dataset.noiseIntensity || '1.75');
  var SCALE           = parseFloat(mount.dataset.scale          || '0.2');
  var ROTATION        = parseFloat(mount.dataset.rotation       || '0');

  // ── Renderer ─────────────────────────────────────────────────────────
  var renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  var canvas = renderer.domElement;
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;pointer-events:none;';
  mount.appendChild(canvas);

  var scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000000);

  var camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
  camera.position.z = 20;

  // ── Resize ───────────────────────────────────────────────────────────
  function resize() {
    var w = mount.clientWidth  || 1;
    var h = mount.clientHeight || 1;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  resize();
  new ResizeObserver(resize).observe(mount);

  // ── Noise GLSL ───────────────────────────────────────────────────────
  var NOISE_GLSL = `
float random(in vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}
float noise(in vec2 st) {
  vec2 i = floor(st);
  vec2 f = fract(st);
  float a = random(i);
  float b = random(i + vec2(1.0, 0.0));
  float c = random(i + vec2(0.0, 1.0));
  float d = random(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}
vec4 permute(vec4 x) { return mod(((x * 34.0) + 1.0) * x, 289.0); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
vec3 fade(vec3 t) { return t * t * t * (t * (t * 6.0 - 15.0) + 10.0); }
float cnoise(vec3 P) {
  vec3 Pi0 = floor(P); vec3 Pi1 = Pi0 + vec3(1.0);
  Pi0 = mod(Pi0, 289.0); Pi1 = mod(Pi1, 289.0);
  vec3 Pf0 = fract(P); vec3 Pf1 = Pf0 - vec3(1.0);
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = Pi0.zzzz; vec4 iz1 = Pi1.zzzz;
  vec4 ixy = permute(permute(ix) + iy);
  vec4 ixy0 = permute(ixy + iz0); vec4 ixy1 = permute(ixy + iz1);
  vec4 gx0 = ixy0 / 7.0; vec4 gy0 = fract(floor(gx0) / 7.0) - 0.5;
  gx0 = fract(gx0); vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
  vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0 * (step(0.0, gx0) - 0.5); gy0 -= sz0 * (step(0.0, gy0) - 0.5);
  vec4 gx1 = ixy1 / 7.0; vec4 gy1 = fract(floor(gx1) / 7.0) - 0.5;
  gx1 = fract(gx1); vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
  vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1 * (step(0.0, gx1) - 0.5); gy1 -= sz1 * (step(0.0, gy1) - 0.5);
  vec3 g000 = vec3(gx0.x, gy0.x, gz0.x); vec3 g100 = vec3(gx0.y, gy0.y, gz0.y);
  vec3 g010 = vec3(gx0.z, gy0.z, gz0.z); vec3 g110 = vec3(gx0.w, gy0.w, gz0.w);
  vec3 g001 = vec3(gx1.x, gy1.x, gz1.x); vec3 g101 = vec3(gx1.y, gy1.y, gz1.y);
  vec3 g011 = vec3(gx1.z, gy1.z, gz1.z); vec3 g111 = vec3(gx1.w, gy1.w, gz1.w);
  vec4 norm0 = taylorInvSqrt(vec4(dot(g000,g000),dot(g010,g010),dot(g100,g100),dot(g110,g110)));
  g000 *= norm0.x; g010 *= norm0.y; g100 *= norm0.z; g110 *= norm0.w;
  vec4 norm1 = taylorInvSqrt(vec4(dot(g001,g001),dot(g011,g011),dot(g101,g101),dot(g111,g111)));
  g001 *= norm1.x; g011 *= norm1.y; g101 *= norm1.z; g111 *= norm1.w;
  float n000 = dot(g000, Pf0);
  float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
  float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
  float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
  float n111 = dot(g111, Pf1);
  vec3 fade_xyz = fade(Pf0);
  vec4 n_z = mix(vec4(n000,n100,n010,n110), vec4(n001,n101,n011,n111), fade_xyz.z);
  vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
  float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
  return 2.2 * n_xyz;
}
`;

  // ── extendMaterial ───────────────────────────────────────────────────
  // Injects custom GLSL into Three.js's built-in physical shader.
  function extendMaterial(cfg) {
    var physical   = THREE.ShaderLib.physical;
    var baseUni    = THREE.UniformsUtils.clone(physical.uniforms);
    var baseDefines = (physical.defines) ? Object.assign({}, physical.defines) : { STANDARD: '', PHYSICAL: '' };

    // Merge custom uniforms (accept { value: x } or bare x)
    Object.keys(cfg.uniforms || {}).forEach(function (key) {
      var u = cfg.uniforms[key];
      baseUni[key] = (u !== null && typeof u === 'object' && 'value' in u) ? u : { value: u };
    });

    var header = cfg.header || '';
    var vert = header + '\n' + (cfg.vertexHeader || '') + '\n' + physical.vertexShader;
    var frag = header + '\n' + physical.fragmentShader;

    Object.keys(cfg.vertex || {}).forEach(function (inc) {
      vert = vert.replace(inc, inc + '\n' + cfg.vertex[inc]);
    });
    Object.keys(cfg.fragment || {}).forEach(function (inc) {
      frag = frag.replace(inc, inc + '\n' + cfg.fragment[inc]);
    });

    return new THREE.ShaderMaterial({
      defines:        baseDefines,
      uniforms:       baseUni,
      vertexShader:   vert,
      fragmentShader: frag,
      lights:         true,
      fog:            false,
      side:           THREE.DoubleSide
    });
  }

  // ── Geometry — stacked planes ────────────────────────────────────────
  function buildGeometry(n, width, height, heightSegments) {
    var spacing     = 0;
    var numVerts    = n * (heightSegments + 1) * 2;
    var numFaces    = n * heightSegments * 2;
    var positions   = new Float32Array(numVerts * 3);
    var uvs         = new Float32Array(numVerts * 2);
    var indices     = new Uint32Array(numFaces * 3);
    var vOff = 0, uvOff = 0, iOff = 0;
    var totalW  = n * width + (n - 1) * spacing;
    var xBase   = -totalW / 2;

    for (var i = 0; i < n; i++) {
      var xOff  = xBase + i * (width + spacing);
      var uvXOff = Math.random() * 300;
      var uvYOff = Math.random() * 300;

      for (var j = 0; j <= heightSegments; j++) {
        var y = height * (j / heightSegments - 0.5);
        positions.set([xOff, y, 0, xOff + width, y, 0], vOff * 3);
        var uvY = j / heightSegments;
        uvs.set([uvXOff, uvY + uvYOff, uvXOff + 1, uvY + uvYOff], uvOff);

        if (j < heightSegments) {
          var a = vOff, b = vOff + 1, c = vOff + 2, d = vOff + 3;
          indices.set([a, b, c, c, b, d], iOff);
          iOff += 6;
        }
        vOff  += 2;
        uvOff += 4;
      }
    }

    var geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('uv',       new THREE.BufferAttribute(uvs,       2));
    geo.setIndex(new THREE.BufferAttribute(indices, 1));
    geo.computeVertexNormals();
    return geo;
  }

  // ── Build material ───────────────────────────────────────────────────
  var mat = extendMaterial({
    header: `
varying vec3 vEye;
varying float vNoise;
varying vec2 vUv;
varying vec3 vPosition;
uniform float time;
uniform float uSpeed;
uniform float uNoiseIntensity;
uniform float uScale;
${NOISE_GLSL}`,
    vertexHeader: `
float getPos(vec3 pos) {
  vec3 noisePos = vec3(pos.x * 0.0, pos.y - uv.y, pos.z + time * uSpeed * 3.0) * uScale;
  return cnoise(noisePos);
}
vec3 getCurrentPos(vec3 pos) {
  vec3 np = pos; np.z += getPos(pos); return np;
}
vec3 getNormal(vec3 pos) {
  vec3 cur    = getCurrentPos(pos);
  vec3 nextX  = getCurrentPos(pos + vec3(0.01, 0.0,  0.0));
  vec3 nextZ  = getCurrentPos(pos + vec3(0.0, -0.01, 0.0));
  vec3 tanX   = normalize(nextX - cur);
  vec3 tanZ   = normalize(nextZ - cur);
  return normalize(cross(tanZ, tanX));
}`,
    vertex: {
      '#include <begin_vertex>':      'transformed.z += getPos(transformed.xyz);',
      '#include <beginnormal_vertex>': 'objectNormal = getNormal(position.xyz);'
    },
    fragment: {
      '#include <dithering_fragment>': `
float randomNoise = noise(gl_FragCoord.xy);
gl_FragColor.rgb -= randomNoise / 15.0 * uNoiseIntensity;`
    },
    uniforms: {
      diffuse:         { value: new THREE.Color(0x000000) },
      roughness:       { value: 0.3 },
      metalness:       { value: 0.3 },
      envMapIntensity: { value: 10 },
      time:            { value: 0 },
      uSpeed:          { value: SPEED },
      uNoiseIntensity: { value: NOISE_INTENSITY },
      uScale:          { value: SCALE }
    }
  });

  // ── Scene assembly ───────────────────────────────────────────────────
  var geo  = buildGeometry(BEAM_NUMBER, BEAM_WIDTH, BEAM_HEIGHT, 100);
  var mesh = new THREE.Mesh(geo, mat);

  var group = new THREE.Group();
  group.rotation.z = ROTATION * (Math.PI / 180);
  group.add(mesh);

  var dirLight = new THREE.DirectionalLight(LIGHT_COLOR, 1);
  dirLight.position.set(0, 3, 10);
  group.add(dirLight);

  scene.add(group);
  scene.add(new THREE.AmbientLight(0xffffff, 1));

  // ── Render loop ──────────────────────────────────────────────────────
  var lastT = performance.now();
  function tick() {
    requestAnimationFrame(tick);
    var now = performance.now();
    mat.uniforms.time.value += 0.1 * (now - lastT) / 1000;
    lastT = now;
    renderer.render(scene, camera);
  }
  tick();

})();
